import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiUser, FiCheck, FiX, FiPlus, FiTrash2, FiEdit, FiLock, FiSettings, FiZap } from 'react-icons/fi';
import { authenticatedFetch } from '../utils/api';
import SetAccessPage from './SetAccessPage';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;

const HeaderBar = styled.div`
  background: transparent;
  border-bottom: none;
  padding: 16px 32px;
  box-shadow: none;
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-height: 60px;
  gap: 16px;
  z-index: 1;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  color: white;
  padding: 6px 16px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  grid-column: 1;
  min-width: fit-content;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const PortalTitle = styled.h1`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  white-space: nowrap;
  grid-column: 2;
  justify-self: center;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
`;

const PageTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const UsersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
`;

const UserCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const UserCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const UserIcon = styled.div`
  width: 50px;
  height: 50px;
  background: #eff6ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-size: 1.5rem;
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 5px;
`;

const UserRole = styled.div`
  font-size: 0.9rem;
  color: #64748b;
`;

const PlantCount = styled.div`
  font-size: 0.95rem;
  color: #64748b;
  margin-bottom: 20px;
  font-weight: 500;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ActionBtn = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  color: #333333;
  padding: 10px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const ConfirmButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  color: #333333;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CancelModalButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  color: #333333;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f5f5f5;
  }
`;

const SelectAllButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  color: #333333;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  color: #1e293b;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
  }

  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  color: #1e293b;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
  }

  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
`;

const PlantsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
  margin-top: 15px;
`;

const PlantSelectButton = styled.button`
  background: white;
  border: 1px solid ${props => props.$selected ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 4px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;
  color: #333333;
  font-weight: 500;

  & > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    flex: 1;
  }

  &:hover {
    background: #f5f5f5;
  }
`;

const PlantCode = styled.div`
  font-size: 0.95rem;
  color: #333333;
  font-weight: 600;
  text-align: left;
  width: 100%;
`;

const PlantName = styled.div`
  font-size: 0.85rem;
  color: ${props => props.$selected ? 'rgba(255,255,255,0.9)' : '#64748b'};
  margin-top: 4px;
  text-align: left;
  width: 100%;
`;

const CheckIcon = styled(FiCheck)`
  color: white;
  font-size: 1.2rem;
`;

const InteractiveLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background: transparent;
  backdrop-filter: none;
  border-radius: 0;
  box-shadow: none;
  min-height: 400px;
  border: none;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 0.3px;
`;

const LoadingProgress = () => {
  return (
    <InteractiveLoadingContainer>
      <LoadingSpinner />
      <LoadingText>Loading Users</LoadingText>
    </InteractiveLoadingContainer>
  );
};

const ErrorMessage = styled.div`
  text-align: center;
  color: #ef4444;
  padding: 40px;
  font-size: 1.1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

// Add User Modal
const AddUserModal = ({ onConfirm, onClose, processing }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (username.trim() && password.trim() && role) {
      onConfirm({ username, password, role });
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add New User</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={18} />
            Close
          </CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              Username
            </label>
            <TextInput
              type="text"
              placeholder="Enter username..."
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={processing}
              autoFocus
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              Password
            </label>
            <TextInput
              type="password"
              placeholder="Enter password..."
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={processing}
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              Role
            </label>
            <SelectInput
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={processing}
            >
              <option value="user">user</option>
              <option value="admin">admin</option>
            </SelectInput>
          </div>
          <ModalActions>
            <ConfirmButton type="submit" disabled={!username.trim() || !password.trim() || !role || processing}>
              {processing ? 'Creating...' : 'Add User'}
            </ConfirmButton>
            <CancelModalButton type="button" onClick={onClose} disabled={processing}>
              Cancel
            </CancelModalButton>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// Change Username Modal
const ChangeUsernameModal = ({ user, onConfirm, onClose, processing }) => {
  const [newUsername, setNewUsername] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newUsername.trim()) {
      onConfirm(newUsername.trim());
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Change Username</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={18} />
            Close
          </CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              Current Username
            </label>
            <TextInput
              type="text"
              value={user.username}
              disabled
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              New Username
            </label>
            <TextInput
              type="text"
              placeholder="Enter new username..."
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              disabled={processing}
              autoFocus
            />
          </div>
          <ModalActions>
            <ConfirmButton type="submit" disabled={!newUsername.trim() || processing}>
              {processing ? 'Changing...' : 'Change Username'}
            </ConfirmButton>
            <CancelModalButton type="button" onClick={onClose} disabled={processing}>
              Cancel
            </CancelModalButton>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// Change Password Modal
const ChangePasswordModal = ({ user, onConfirm, onClose, processing }) => {
  const [newPassword, setNewPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newPassword.trim()) {
      onConfirm(newPassword.trim());
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Change Password</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={18} />
            Close
          </CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              User
            </label>
            <TextInput
              type="text"
              value={user.username}
              disabled
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              New Password
            </label>
            <TextInput
              type="password"
              placeholder="Enter new password..."
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={processing}
              autoFocus
            />
          </div>
          <ModalActions>
            <ConfirmButton type="submit" disabled={!newPassword.trim() || processing}>
              {processing ? 'Changing...' : 'Change Password'}
            </ConfirmButton>
            <CancelModalButton type="button" onClick={onClose} disabled={processing}>
              Cancel
            </CancelModalButton>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// Remove User Modal
const RemoveUserModal = ({ users, onConfirm, onClose, processing }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmUsername, setConfirmUsername] = useState('');

  // Filter out admin users
  const nonAdminUsers = users.filter(user => user.role !== 'admin');

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setConfirmUsername('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedUser && confirmUsername.trim() === selectedUser.username) {
      onConfirm(selectedUser);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Delete User</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={18} />
            Close
          </CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              Select User to Delete
            </label>
            <SelectInput
              value={selectedUser ? selectedUser.username : ''}
              onChange={(e) => {
                const user = nonAdminUsers.find(u => u.username === e.target.value);
                handleUserSelect(user || null);
              }}
              disabled={processing}
            >
              <option value="">-- Select a user --</option>
              {nonAdminUsers.map(user => (
                <option key={user.username} value={user.username}>
                  {user.username} ({user.role})
                </option>
              ))}
            </SelectInput>
          </div>
          {selectedUser && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
                Type the username to confirm deletion: <strong>{selectedUser.username}</strong>
              </label>
              <TextInput
                type="text"
                placeholder={`Type "${selectedUser.username}" to confirm`}
                value={confirmUsername}
                onChange={(e) => setConfirmUsername(e.target.value)}
                disabled={processing}
                autoFocus
              />
            </div>
          )}
          <ModalActions>
            <ConfirmButton 
              type="submit" 
              disabled={!selectedUser || confirmUsername.trim() !== selectedUser.username || processing}
              style={{ background: '#ef4444' }}
            >
              {processing ? 'Deleting...' : 'Delete User'}
            </ConfirmButton>
            <CancelModalButton type="button" onClick={onClose} disabled={processing}>
              Cancel
            </CancelModalButton>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// Add Plant Modal
const AddPlantModal = ({ user, allPlants, userPlants, onConfirm, onClose, processing }) => {
  const [selectedPlants, setSelectedPlants] = useState([]);

  const availablePlants = allPlants.filter(plant => 
    !userPlants.some(up => up.id.toUpperCase() === plant.id.toUpperCase())
  );

  const togglePlant = (plantId) => {
    setSelectedPlants(prev => 
      prev.includes(plantId) 
        ? prev.filter(id => id !== plantId)
        : [...prev, plantId]
    );
  };

  const handleSelectAll = () => {
    const allPlantIds = availablePlants.map(plant => plant.id.toUpperCase());
    const allSelected = allPlantIds.length > 0 && allPlantIds.every(id => selectedPlants.includes(id));
    
    if (allSelected) {
      // Deselect all
      setSelectedPlants([]);
    } else {
      // Select all
      setSelectedPlants(allPlantIds);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedPlants.length > 0) {
      onConfirm(selectedPlants);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add Plants to {user.username}</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={18} />
            Close
          </CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              Select Plants ({selectedPlants.length} selected)
            </label>
            <PlantsGrid>
              {availablePlants.map(plant => (
                <PlantSelectButton
                  key={plant.id}
                  type="button"
                  $selected={selectedPlants.includes(plant.id.toUpperCase())}
                  onClick={() => togglePlant(plant.id.toUpperCase())}
                  disabled={processing}
                >
                  <div>
                    <PlantCode $selected={selectedPlants.includes(plant.id.toUpperCase())}>
                      {plant.id}
                    </PlantCode>
                    <PlantName $selected={selectedPlants.includes(plant.id.toUpperCase())}>
                      {plant.name || `${plant.id} Plant`}
                    </PlantName>
                  </div>
                  {selectedPlants.includes(plant.id.toUpperCase()) && <CheckIcon />}
                </PlantSelectButton>
              ))}
            </PlantsGrid>
            {availablePlants.length === 0 && (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
                All available plants are already assigned to this user.
              </div>
            )}
          </div>
          <ModalActions>
            <SelectAllButton 
              type="button" 
              onClick={handleSelectAll} 
              disabled={availablePlants.length === 0 || processing}
            >
              Select All
            </SelectAllButton>
            <ConfirmButton type="submit" disabled={selectedPlants.length === 0 || processing}>
              {processing ? 'Adding...' : `Add ${selectedPlants.length} Plant(s)`}
            </ConfirmButton>
            <CancelModalButton type="button" onClick={onClose} disabled={processing}>
              Cancel
            </CancelModalButton>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// Remove Plant Modal
const RemovePlantModal = ({ user, userPlants, onConfirm, onClose, processing }) => {
  const [selectedPlants, setSelectedPlants] = useState([]);

  const togglePlant = (plantId) => {
    setSelectedPlants(prev => 
      prev.includes(plantId) 
        ? prev.filter(id => id !== plantId)
        : [...prev, plantId]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedPlants.length > 0) {
      onConfirm(selectedPlants);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Remove Plants from {user.username}</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={18} />
            Close
          </CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#1e293b' }}>
              Select Plants to Remove ({selectedPlants.length} selected)
            </label>
            <PlantsGrid>
              {userPlants.map(plant => (
                <PlantSelectButton
                  key={plant.id}
                  type="button"
                  $selected={selectedPlants.includes(plant.id.toUpperCase())}
                  onClick={() => togglePlant(plant.id.toUpperCase())}
                  disabled={processing}
                >
                  <div>
                    <PlantCode $selected={selectedPlants.includes(plant.id.toUpperCase())}>
                      {plant.id}
                    </PlantCode>
                    <PlantName $selected={selectedPlants.includes(plant.id.toUpperCase())}>
                      {plant.name || `${plant.id} Plant`}
                    </PlantName>
                  </div>
                  {selectedPlants.includes(plant.id.toUpperCase()) && <CheckIcon />}
                </PlantSelectButton>
              ))}
            </PlantsGrid>
            {userPlants.length === 0 && (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
                This user has no assigned plants.
              </div>
            )}
          </div>
          <ModalActions>
            <ConfirmButton type="submit" disabled={selectedPlants.length === 0 || processing} style={{ background: '#2563eb' }}>
              {processing ? 'Removing...' : `Remove ${selectedPlants.length} Plant(s)`}
            </ConfirmButton>
            <CancelModalButton type="button" onClick={onClose} disabled={processing} style={{ background: '#2563eb' }}>
              Cancel
            </CancelModalButton>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// See Plants Modal
const SeePlantsModal = ({ user, userPlants, onClose }) => {
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Plants for {user.username}</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={18} />
            Close
          </CloseButton>
        </ModalHeader>
        <div>
          <div style={{ marginBottom: '15px', fontWeight: 600, color: '#1e293b' }}>
            Total: {userPlants.length} plant(s)
          </div>
          <PlantsGrid>
            {userPlants.map(plant => (
              <div
                key={plant.id}
                style={{
                  background: 'white',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                }}
              >
                <PlantCode $selected={false}>{plant.id}</PlantCode>
                <PlantName $selected={false}>{plant.name || `${plant.id} Plant`}</PlantName>
              </div>
            ))}
          </PlantsGrid>
          {userPlants.length === 0 && (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
              This user has no assigned plants.
            </div>
          )}
        </div>
        <ModalActions>
          <CancelModalButton type="button" onClick={onClose}>
            Close
          </CancelModalButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

const AdminPage = ({ onBack, onSetAccessPageChange }) => {
  const [users, setUsers] = useState([]);
  const [allPlants, setAllPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showChangeUsername, setShowChangeUsername] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(null);
  const [showAddPlant, setShowAddPlant] = useState(null);
  const [showRemovePlant, setShowRemovePlant] = useState(null);
  const [showSeePlants, setShowSeePlants] = useState(null);
  const [showSetAccess, setShowSetAccess] = useState(null);
  const [showRemoveUser, setShowRemoveUser] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch('/api/admin/users');
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
        setAllPlants(data.allPlants);
      } else {
        setError(data.error || 'Failed to fetch users');
      }
    } catch (err) {
      setError('Failed to load users. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (userData) => {
    setProcessing(true);
    try {
      const response = await authenticatedFetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();
      if (data.success) {
        alert('User created successfully!');
        setShowAddUser(false);
        fetchUsers();
      } else {
        alert(data.error || 'Failed to create user');
      }
    } catch (err) {
      alert('Failed to create user');
    } finally {
      setProcessing(false);
    }
  };

  const handleChangeUsername = async (newUsername) => {
    const user = showChangeUsername;
    setProcessing(true);
    try {
      const response = await authenticatedFetch(`/api/admin/users/${user.username}/username`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newUsername }),
      });

      const data = await response.json();
      if (data.success) {
        alert('Username changed successfully!');
        setShowChangeUsername(null);
        fetchUsers();
      } else {
        alert(data.error || 'Failed to change username');
      }
    } catch (err) {
      alert('Failed to change username');
    } finally {
      setProcessing(false);
    }
  };

  const handleChangePassword = async (newPassword) => {
    const user = showChangePassword;
    setProcessing(true);
    try {
      const response = await authenticatedFetch(`/api/admin/users/${user.username}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newPassword }),
      });

      const data = await response.json();
      if (data.success) {
        alert('Password changed successfully!');
        setShowChangePassword(null);
      } else {
        alert(data.error || 'Failed to change password');
      }
    } catch (err) {
      alert('Failed to change password');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddPlants = async (plantIds) => {
    const user = showAddPlant;
    setProcessing(true);
    try {
      for (const plantId of plantIds) {
        const response = await authenticatedFetch(`/api/admin/users/${user.username}/plants`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ plantId }),
        });

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || 'Failed to add plant');
        }
      }
      alert(`Successfully added ${plantIds.length} plant(s)!`);
      setShowAddPlant(null);
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to add plants');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemovePlants = async (plantIds) => {
    const user = showRemovePlant;
    setProcessing(true);
    try {
      for (const plantId of plantIds) {
        const response = await authenticatedFetch(`/api/admin/users/${user.username}/plants/${plantId}`, {
          method: 'DELETE',
        });

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || 'Failed to remove plant');
        }
      }
      alert(`Successfully removed ${plantIds.length} plant(s)!`);
      setShowRemovePlant(null);
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to remove plants');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveUser = async (user) => {
    setProcessing(true);
    try {
      const response = await authenticatedFetch(`/api/admin/users/${user.username}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        alert('User deleted successfully!');
        setShowRemoveUser(false);
        fetchUsers();
      } else {
        alert(data.error || 'Failed to delete user');
      }
    } catch (err) {
      alert('Failed to delete user');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (onSetAccessPageChange) {
      onSetAccessPageChange(!!showSetAccess);
    }
  }, [showSetAccess, onSetAccessPageChange]);

  if (showSetAccess) {
    return <SetAccessPage user={showSetAccess} onBack={() => setShowSetAccess(null)} />;
  }

  return (
    <PageContainer>
      <MainContent>
        <ContentWrapper>
          <PageHeader>
            <PageTitle>User Management</PageTitle>
          </PageHeader>

          {loading ? (
            <LoadingProgress />
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <UsersGrid>
              {users.map((user) => (
                <UserCard key={user.username}>
                  <UserCardHeader>
                    <UserIcon>
                      <FiUser />
                    </UserIcon>
                    <UserDetails>
                      <UserName>{user.username}</UserName>
                      {user.role.toLowerCase() !== user.username.toLowerCase() && (
                        <UserRole>{user.role}</UserRole>
                      )}
                    </UserDetails>
                  </UserCardHeader>
                  <PlantCount>{user.plants.length} plant(s) assigned</PlantCount>
                  <ActionsContainer>
                    <ActionBtn $variant="primary" onClick={() => setShowSeePlants(user)}>
                      <FiUser size={16} />
                      See Plants
                    </ActionBtn>
                    <ActionBtn $variant="warning" onClick={() => setShowChangeUsername(user)}>
                      <FiEdit size={16} />
                      Change Username
                    </ActionBtn>
                    <ActionBtn $variant="warning" onClick={() => setShowChangePassword(user)}>
                      <FiLock size={16} />
                      Change Password
                    </ActionBtn>
                    {user.role === 'admin' && (
                      <>
                        <ActionBtn $variant="success" onClick={() => setShowAddUser(true)}>
                          <FiPlus size={16} />
                          Add User
                        </ActionBtn>
                        <ActionBtn $variant="danger" onClick={() => setShowRemoveUser(true)}>
                          <FiTrash2 size={16} />
                          Remove User
                        </ActionBtn>
                      </>
                    )}
                    {user.role !== 'admin' && (
                      <>
                        <ActionBtn $variant="primary" onClick={() => setShowSetAccess(user)}>
                          <FiSettings size={16} />
                          Set Access
                        </ActionBtn>
                        <ActionBtn $variant="success" onClick={() => setShowAddPlant(user)}>
                          <FiPlus size={16} />
                          Add Plant
                        </ActionBtn>
                        <ActionBtn $variant="danger" onClick={() => setShowRemovePlant(user)}>
                          <FiTrash2 size={16} />
                          Remove Plant
                        </ActionBtn>
                      </>
                    )}
                  </ActionsContainer>
                </UserCard>
              ))}
            </UsersGrid>
          )}

          {showAddUser && (
            <AddUserModal
              onConfirm={handleAddUser}
              onClose={() => setShowAddUser(false)}
              processing={processing}
            />
          )}

          {showChangeUsername && (
            <ChangeUsernameModal
              user={showChangeUsername}
              onConfirm={handleChangeUsername}
              onClose={() => setShowChangeUsername(null)}
              processing={processing}
            />
          )}

          {showChangePassword && (
            <ChangePasswordModal
              user={showChangePassword}
              onConfirm={handleChangePassword}
              onClose={() => setShowChangePassword(null)}
              processing={processing}
            />
          )}

          {showAddPlant && (
            <AddPlantModal
              user={showAddPlant}
              allPlants={allPlants}
              userPlants={showAddPlant.plants}
              onConfirm={handleAddPlants}
              onClose={() => setShowAddPlant(null)}
              processing={processing}
            />
          )}

          {showRemovePlant && (
            <RemovePlantModal
              user={showRemovePlant}
              userPlants={showRemovePlant.plants}
              onConfirm={handleRemovePlants}
              onClose={() => setShowRemovePlant(null)}
              processing={processing}
            />
          )}

          {showSeePlants && (
            <SeePlantsModal
              user={showSeePlants}
              userPlants={showSeePlants.plants}
              onClose={() => setShowSeePlants(null)}
            />
          )}

          {showRemoveUser && (
            <RemoveUserModal
              users={users}
              onConfirm={handleRemoveUser}
              onClose={() => setShowRemoveUser(false)}
              processing={processing}
            />
          )}
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default AdminPage;

